import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { REPORT_TYPES } from '@/lib/report-utils';

interface Report {
  id: string;
  nomorLaporan: string;
  projek: string;
  jenis: string;
  tarikhMula: string;
  tarikhAkhir: string;
  oleh: string;
  kandungan: string;
  status: 'Draf' | 'Selesai' | 'Disetujui';
}

interface ReportFormProps {
  report?: Report;
  nomorLaporan: string;
  projects?: any[];
  onSubmit: (data: Report) => void;
  onCancel: () => void;
}

export const ReportForm = ({ report, nomorLaporan, projects = [], onSubmit, onCancel }: ReportFormProps) => {
  const [formData, setFormData] = useState<Partial<Report>>(
    report || {
      id: Date.now().toString(),
      nomorLaporan: nomorLaporan,
      projek: '',
      jenis: 'Harian',
      tarikhMula: new Date().toISOString().split('T')[0],
      tarikhAkhir: new Date().toISOString().split('T')[0],
      oleh: '',
      kandungan: '',
      status: 'Draf',
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.projek?.trim()) newErrors.projek = 'Pilih projek diperlukan';
    if (!formData.tarikhMula?.trim()) newErrors.tarikhMula = 'Tarikh mula diperlukan';
    if (!formData.tarikhAkhir?.trim()) newErrors.tarikhAkhir = 'Tarikh akhir diperlukan';
    if (!formData.oleh?.trim()) newErrors.oleh = 'Nama pelapor diperlukan';
    if (!formData.kandungan?.trim()) newErrors.kandungan = 'Kandungan laporan diperlukan';

    // Validate dates
    if (formData.tarikhMula && formData.tarikhAkhir) {
      const startDate = new Date(formData.tarikhMula);
      const endDate = new Date(formData.tarikhAkhir);
      if (endDate < startDate) {
        newErrors.tarikhAkhir = 'Tarikh akhir harus selepas tarikh mula';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData as Report);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Laporan Baru</CardTitle>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombor Laporan (Auto) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nombor Laporan</label>
              <Input
                type="text"
                value={formData.nomorLaporan || ''}
                disabled
                className="bg-slate-100 text-slate-500"
              />
              <p className="text-xs text-slate-500">Nombor akan di-jana secara automatik</p>
            </div>

            {/* Pilih Projek */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Projek (untuk semak auto-selesei) *</label>
              <select
                name="projek"
                value={formData.projek || ''}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.projek ? 'border-red-500' : 'border-slate-300'
                }`}
              >
                <option value="">Pilih projek (pilihan)</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.namaProjek}>
                    {p.namaProjek} ({p.kodProjek})
                  </option>
                ))}
              </select>
              {errors.projek && <p className="text-sm text-red-600">{errors.projek}</p>}
            </div>

            {/* Jenis Laporan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Jenis</label>
              <select
                name="jenis"
                value={formData.jenis || 'Harian'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {REPORT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Tarikh Mula & Tarikh Akhir */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Tarikh *</label>
                <Input
                  type="date"
                  name="tarikhMula"
                  value={formData.tarikhMula || ''}
                  onChange={handleChange}
                  className={errors.tarikhMula ? 'border-red-500' : ''}
                />
                {errors.tarikhMula && <p className="text-sm text-red-600">{errors.tarikhMula}</p>}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Tarikh Akhir *</label>
                <Input
                  type="date"
                  name="tarikhAkhir"
                  value={formData.tarikhAkhir || ''}
                  onChange={handleChange}
                  className={errors.tarikhAkhir ? 'border-red-500' : ''}
                />
                {errors.tarikhAkhir && <p className="text-sm text-red-600">{errors.tarikhAkhir}</p>}
              </div>
            </div>

            {/* Nama Pelapor */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Oleh *</label>
              <Input
                type="text"
                name="oleh"
                value={formData.oleh || ''}
                onChange={handleChange}
                placeholder="Nama pelapor / penyelia"
                className={errors.oleh ? 'border-red-500' : ''}
              />
              {errors.oleh && <p className="text-sm text-red-600">{errors.oleh}</p>}
            </div>

            {/* Kandungan Laporan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Kandungan *</label>
              <textarea
                name="kandungan"
                value={formData.kandungan || ''}
                onChange={handleChange}
                placeholder="Deskripsikan aktivitas kerja, kemajuan, isu, dan catatan penting..."
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.kandungan ? 'border-red-500' : 'border-slate-300'
                }`}
              />
              {errors.kandungan && <p className="text-sm text-red-600">{errors.kandungan}</p>}
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                name="status"
                value={formData.status || 'Draf'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Draf">Draf</option>
                <option value="Selesai">Selesai</option>
                <option value="Disetujui">Disetujui</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                Simpan
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Batal
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
